<template>
  <!-- Yan menü. Menü verisi /api/VegaCommon/GetSideBarMenu (iki seviye: bağlantı ya da grup + alt
       bağlantılar). Aktiflik route'tan türer; açık grubun tek doğruluk kaynağı `openKey`'dir (akordeon).
       Daraltma ve mobil çekmece durumu Layout'tadır. Görünüm assets/css/sidebar.css. -->
  <aside ref="root"
         class="vega-sidebar vega-ambient"
         :class="{ 'is-rail': rail, 'is-drawer-open': drawerOpen }"
         :aria-label="$t('LeftMenu.AriaLabel')">
    <div class="vega-sidebar__brand">
      <router-link :to="homeURL" class="vega-sidebar__logo">
        <img v-if="rail" :src="logoIcon" class="vega-sidebar__logo-icon" alt="Vega">
        <img v-else :src="logoFull" class="vega-sidebar__logo-full" alt="Vega">
      </router-link>
      <button type="button"
              class="vega-sidebar__close"
              :aria-label="$t('Close')"
              :title="$t('Close')"
              @click="$emit('close')">
        <i class="fa-regular fa-xmark" aria-hidden="true"></i>
      </button>
    </div>

    <nav ref="nav" class="iq-sidebar-menu vega-sidebar__nav" @scroll.passive="closeFlyout()">
      <ul id="sidebar" class="vega-nav">
        <li v-for="item in items"
            :key="item.name"
            class="vega-nav__item"
            :class="{ 'is-open': !rail && isOpen(item), 'has-active': isGroupActive(item) }">

          <router-link v-if="!hasChildren(item)"
                       class="vega-nav__row"
                       :class="{ 'is-active': isLinkActive(item.link) }"
                       :to="item.link"
                       :title="rail ? $t(item.title) : null"
                       :aria-current="isLinkActive(item.link) ? 'page' : null"
                       @pointerenter="closeFlyout()">
            <i class="vega-nav__icon" :class="$faIcon(item.icon)" aria-hidden="true"></i>
            <span class="vega-nav__label">{{ $t(item.title) }}</span>
          </router-link>

          <template v-else>
            <!-- Smoke seçicisi `#sidebar > li > a[aria-controls]`: grup satırı bağlantı biçiminde kalır.
                 `href="#"` orta tuşla bulunulan sayfayı (ör. canlı görüşme) yeni sekmede yeniden
                 açıyordu; grubun sayfası varsa o, yoksa hiç adres verilmez. -->
            <a :ref="el => setTrigger(item.name, el)"
               :href="groupHref(item)"
               :tabindex="groupHref(item) ? null : 0"
               role="button"
               class="vega-nav__row"
               :class="[isOpen(item) && !rail ? 'not-collapsed' : 'collapsed', { 'is-flyout': flyoutKey === item.name }]"
               :aria-controls="rail ? 'vega-sidebar-flyout' : item.name"
               :aria-expanded="(rail ? flyoutKey === item.name : isOpen(item)) ? 'true' : 'false'"
               :aria-label="rail ? $t(item.title) : null"
               @click="onGroupRowClick(item, $event)"
               @keydown.enter.prevent="onGroupClick(item)"
               @keydown.space.prevent="onGroupClick(item)"
               @pointerenter="onGroupPointerEnter(item, $event)"
               @pointerleave="onFlyoutPointerLeave">
              <i class="vega-nav__icon" :class="$faIcon(item.icon)" aria-hidden="true"></i>
              <span class="vega-nav__label">{{ $t(item.title) }}</span>
              <i class="vega-nav__caret fa-regular fa-chevron-right" aria-hidden="true"></i>
            </a>
            <transition enter-active-class="is-animating" leave-active-class="is-animating"
                        @enter="onSubEnter" @after-enter="clearHeight" @leave="onSubLeave" @after-leave="clearHeight">
              <ul v-show="!rail && isOpen(item)"
                  :id="item.name"
                  class="iq-submenu vega-nav__sub"
                  :class="{ show: !rail && isOpen(item) }">
                <li v-for="child in item.children" :key="child.name" :class="{ active: isLinkActive(child.link) }">
                  <router-link class="vega-nav__subrow"
                               :to="child.link"
                               :title="$t(child.title)"
                               :aria-current="isLinkActive(child.link) ? 'page' : null">
                    <span class="vega-nav__label">{{ $t(child.title) }}</span>
                  </router-link>
                </li>
              </ul>
            </transition>
          </template>
        </li>
      </ul>
    </nav>

    <div class="vega-sidebar__foot">
      <button type="button"
              class="vega-nav__row vega-sidebar__toggle"
              :aria-expanded="rail ? 'false' : 'true'"
              :aria-label="rail ? $t('LeftMenu.Expand') : $t('LeftMenu.Collapse')"
              :title="rail ? $t('LeftMenu.Expand') : $t('LeftMenu.Collapse')"
              @click="toggleRail">
        <i class="vega-nav__icon fa-regular" :class="rail ? 'fa-angles-right' : 'fa-angles-left'" aria-hidden="true"></i>
        <span class="vega-nav__label">{{ $t('LeftMenu.Collapse') }}</span>
      </button>
    </div>

    <!-- Daraltılmış rayda grubun bağlantıları: ikonun yanında açılan tek panel, sayfayı itmez. -->
    <div v-if="rail && flyoutGroup"
         id="vega-sidebar-flyout"
         ref="flyout"
         class="vega-flyout"
         role="group"
         :aria-label="$t(flyoutGroup.title)"
         :style="{ top: flyoutTop + 'px' }"
         @pointerenter="cancelFlyoutClose"
         @pointerleave="onFlyoutPointerLeave">
      <div class="vega-flyout__title">{{ $t(flyoutGroup.title) }}</div>
      <router-link v-for="child in flyoutGroup.children"
                   :key="child.name"
                   class="vega-flyout__link"
                   :class="{ 'is-active': isLinkActive(child.link) }"
                   :to="child.link"
                   :aria-current="isLinkActive(child.link) ? 'page' : null">
        <i class="vega-flyout__icon" :class="$faIcon(child.icon)" aria-hidden="true"></i>
        <span class="vega-flyout__label">{{ $t(child.title) }}</span>
      </router-link>
    </div>
  </aside>
</template>

<script>
  import layoutService from '../../../services/layout'

  const EDGE_GAP = 12
  const FLYOUT_CLOSE_DELAY = 220

  export default {
    name: 'SideBarStyle',
    emits: ['toggle-rail', 'close'],
    props: {
      homeURL: { type: Object, default: () => ({ name: 'home.dashboard' }) },
      logoFull: { type: String, default: require('../../../assets/images/logo-wordmark.png') },
      logoIcon: { type: String, default: require('../../../assets/images/logo-icon.png') },
      rail: { type: Boolean, default: false },
      drawerOpen: { type: Boolean, default: false }
    },
    data() {
      return {
        items: [],
        openKey: null,
        flyoutKey: null,
        flyoutTop: 0,
        flyoutByHover: false
      }
    },
    computed: {
      flyoutGroup() {
        return this.items.find(item => item.name === this.flyoutKey) || null
      }
    },
    watch: {
      '$route.fullPath'() {
        this.closeFlyout()
        // Menüde karşılığı olmayan sayfada (ör. danışan özeti) açık grup korunur.
        const active = this.activeGroupKey()
        if (active) this.openKey = active
        this.$nextTick(this.revealActive)
      },
      rail() {
        this.closeFlyout()
      }
    },
    methods: {
      hasChildren(item) {
        return Array.isArray(item.children) && item.children.length > 0
      },
      // `matched`: alt route (ayar sekmesi, modal) açıkken üst kayıt da eşleşir.
      isLinkActive(link) {
        const name = link && link.name
        return !!name && this.$route.matched.some(record => record.name === name)
      },
      isGroupActive(item) {
        return this.hasChildren(item)
          ? item.children.some(child => this.isLinkActive(child.link))
          : this.isLinkActive(item.link)
      },
      isOpen(item) {
        return this.openKey === item.name
      },
      groupHref(item) {
        if (!item.link || !item.link.name) return null
        try {
          return this.$router.resolve(item.link).href
        } catch (error) {
          return null
        }
      },
      // Ctrl/Cmd/Shift + tık tarayıcıya bırakılır (sayfayı yeni sekme/pencerede açar);
      // normal tıklama alt menüyü açar.
      onGroupRowClick(item, event) {
        if (this.groupHref(item) && (event.ctrlKey || event.metaKey || event.shiftKey)) return
        event.preventDefault()
        this.onGroupClick(item)
      },
      activeGroupKey() {
        const group = this.items.find(item => this.hasChildren(item) && this.isGroupActive(item))
        return group ? group.name : null
      },
      // Aktif grup kullanıcı kapatınca kapalı kalır; sonraki sayfa değişiminde yeniden açılır.
      onGroupClick(item) {
        if (!this.rail) {
          this.openKey = this.openKey === item.name ? null : item.name
          return
        }
        // Fareyle gelinince panel zaten açıktır; ardından gelen tıklama onu kapatmaz.
        if (this.flyoutKey === item.name && !this.flyoutByHover) this.closeFlyout()
        else this.openFlyout(item, true)
      },
      toggleRail() {
        this.closeFlyout()
        this.$emit('toggle-rail')
      },
      onSubEnter(el) {
        el.style.height = '0px'
        void el.offsetHeight
        el.style.height = el.scrollHeight + 'px'
      },
      onSubLeave(el) {
        el.style.height = el.scrollHeight + 'px'
        void el.offsetHeight
        el.style.height = '0px'
      },
      clearHeight(el) {
        el.style.height = ''
      },
      // Aktif satır görünür kalsın; yalnız menü listesi kayar (scrollIntoView sayfayı da kaydırırdı).
      revealActive() {
        const nav = this.$refs.nav
        const active = nav && nav.querySelector('.vega-nav__row.is-active, .vega-nav__sub li.active, .has-active > .vega-nav__row')
        if (!active) return
        const navRect = nav.getBoundingClientRect()
        const rect = active.getBoundingClientRect()
        if (rect.top >= navRect.top && rect.bottom <= navRect.bottom) return
        nav.scrollTop += rect.top - navRect.top - (nav.clientHeight - rect.height) / 2
      },
      setTrigger(key, el) {
        if (!this.triggers) this.triggers = {}
        if (el) this.triggers[key] = el
        else delete this.triggers[key]
      },
      onGroupPointerEnter(item, event) {
        this.cancelFlyoutClose()
        if (!this.rail || event.pointerType !== 'mouse') return
        this.openFlyout(item, false)
        this.flyoutByHover = true
      },
      async openFlyout(item, focusFirst) {
        this.cancelFlyoutClose()
        this.flyoutByHover = false
        const trigger = this.triggers && this.triggers[item.name]
        this.flyoutTop = trigger ? trigger.getBoundingClientRect().top : EDGE_GAP
        this.flyoutKey = item.name
        await this.$nextTick()
        const panel = this.$refs.flyout
        if (!panel) return
        // Panel pencerenin altından taşıyorsa yukarı kaydırılır.
        const maxTop = window.innerHeight - panel.offsetHeight - EDGE_GAP
        this.flyoutTop = Math.max(EDGE_GAP, Math.min(this.flyoutTop, maxTop))
        if (focusFirst) {
          const link = panel.querySelector('a')
          if (link) link.focus()
        }
      },
      closeFlyout(restoreFocus = false) {
        this.cancelFlyoutClose()
        const key = this.flyoutKey
        this.flyoutKey = null
        this.flyoutByHover = false
        if (restoreFocus && key && this.triggers && this.triggers[key]) this.triggers[key].focus()
      },
      cancelFlyoutClose() {
        clearTimeout(this.flyoutTimer)
        this.flyoutTimer = null
      },
      onFlyoutPointerLeave(event) {
        if (!this.flyoutKey || event.pointerType !== 'mouse') return
        this.cancelFlyoutClose()
        this.flyoutTimer = setTimeout(() => {
          const root = this.$refs.root
          // Klavye odağı paneldeyse kapanmaz.
          if (root && root.querySelector('.vega-flyout :focus-visible')) return
          this.closeFlyout()
        }, FLYOUT_CLOSE_DELAY)
      },
      onKeyDown(event) {
        if (event.key !== 'Escape' || !this.flyoutKey) return
        event.preventDefault()
        const focusInside = this.$refs.root && this.$refs.root.contains(document.activeElement)
        this.closeFlyout(focusInside)
      },
      onOutsidePointer(event) {
        if (this.flyoutKey && this.$refs.root && !this.$refs.root.contains(event.target)) this.closeFlyout()
      },
      onFocusOut(event) {
        if (this.flyoutKey && event.relatedTarget && this.$refs.root && !this.$refs.root.contains(event.relatedTarget)) {
          this.closeFlyout()
        }
      },
      onResize() {
        this.closeFlyout()
      },
      getSideBarMenuItems() {
        layoutService.getSideBarMenuItems()
          .then((response) => {
            this.items = Array.isArray(response) ? response : []
            this.openKey = this.activeGroupKey()
            this.$nextTick(this.revealActive)
          })
      }
    },
    mounted() {
      this.getSideBarMenuItems()
      document.addEventListener('pointerdown', this.onOutsidePointer)
      document.addEventListener('keydown', this.onKeyDown)
      this.$refs.root.addEventListener('focusout', this.onFocusOut)
      window.addEventListener('resize', this.onResize)
    },
    beforeUnmount() {
      this.cancelFlyoutClose()
      document.removeEventListener('pointerdown', this.onOutsidePointer)
      document.removeEventListener('keydown', this.onKeyDown)
      if (this.$refs.root) this.$refs.root.removeEventListener('focusout', this.onFocusOut)
      window.removeEventListener('resize', this.onResize)
    }
  }
</script>

<style src="../../../assets/css/ambient.css"></style>
<style src="../../../assets/css/sidebar.css"></style>
